import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, FormControl, Select, MenuItem, Button } from '@mui/material';
import { useState } from 'react';
import MaterialButton from './maker/component/MaterialButton';
import LineBreak from './maker/component/LineBreak';
import { analyticsManager } from '.';
import { browserLocales } from './player/Utils';

const DAYS_OF_WEEK = [
    'lundi',
    'mardi',
    'mercredi',
    'jeudi',
    'vendredi',
    'samedi',
    'dimanche'
];

const TIME_SLOTS = Array.from({ length: 24 * 2 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? '00' : '30';
    return `${hour.toString().padStart(2, '0')}:${minute}`;
});

const formatTime = (time, language) => {
    if (language === 'en') {
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
    }
    return time;
};

function RestaurantHoursDialog(props) {
    const { t } = useTranslation();

    const convertInitialHours = (initialHours) => {
        if (!initialHours || !initialHours.weekdayDescriptions) {
            return DAYS_OF_WEEK.map(day => ({
                day,
                open: true,
                periods: [{ open: { time: '09:00' }, close: { time: '22:00' } }]
            }));
        }

        return DAYS_OF_WEEK.map(day => {
            const dayDescription = initialHours.weekdayDescriptions.find(desc => 
                desc && desc.toLowerCase().startsWith(day.toLowerCase())
            );

            if (!dayDescription) {
                return {
                    day,
                    open: true,
                    periods: [{ open: { time: '09:00' }, close: { time: '22:00' } }]
                };
            }

            const hoursPart = dayDescription.split(': ')[1];
            
            if (hoursPart === 'Fermé' || hoursPart === 'Closed') {
                return {
                    day,
                    open: false,
                    periods: [{ open: { time: '09:00' }, close: { time: '22:00' } }]
                };
            }

            try {                
                const periods = hoursPart.split(',').map(period => {
                    const [openTime, closeTime] = period.split('–').map(time => {                        
                        const cleanedTime = time.trim();                        
                        const [hours, minutes] = cleanedTime.split(':');
                        return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
                    });

                    return {
                        open: { time: openTime },
                        close: { time: closeTime }
                    };
                });

                return {
                    day,
                    open: true,
                    periods: periods.length > 0 ? periods : [{ open: { time: '09:00' }, close: { time: '22:00' } }]
                };
            } catch (error) {
                console.error('Error parsing hours for day:', day, error);
                return {
                    day,
                    open: true,
                    periods: [{ open: { time: '09:00' }, close: { time: '22:00' } }]
                };
            }
        });
    };

    const [hours, setHours] = useState(() => {
        if (props.initialHours) {
            return convertInitialHours(props.initialHours);
        }
        return DAYS_OF_WEEK.map(day => ({
            day,
            open: true,
            periods: [{ open: { time: '09:00' }, close: { time: '22:00' } }]
        }));
    });

    const handleDayChange = (dayIndex, field, value) => {
        const newHours = [...hours];
        newHours[dayIndex] = {
            ...newHours[dayIndex],
            [field]: value
        };
        analyticsManager.logSelectHoursOpeningTag(props.storyId);
        setHours(newHours);
    };

    const handlePeriodChange = (dayIndex, periodIndex, field, value) => {
        const newHours = [...hours];
        newHours[dayIndex].periods[periodIndex] = {
            ...newHours[dayIndex].periods[periodIndex],
            [field]: { time: value }
        };
        analyticsManager.logSelectHoursHoursTag(props.storyId);
        setHours(newHours);
    };

    const addPeriod = (dayIndex) => {
        const newHours = [...hours];
        newHours[dayIndex].periods.push({
            open: { time: '09:00' },
            close: { time: '22:00' }
        });
        analyticsManager.logAddHoursPeriodTag(props.storyId);
        setHours(newHours);
    };

    const removePeriod = (dayIndex, periodIndex) => {
        const newHours = [...hours];
        newHours[dayIndex].periods.splice(periodIndex, 1);
        setHours(newHours);
    };

    const handleSave = () => {       
        const googlePlacesFormat = {
            weekdayDescriptions: []
        };

        hours.forEach((day) => {
            if (day.open) {
                const periodsText = day.periods.map(period => {
                    const openTime = period.open.time;
                    const closeTime = period.close.time;
                    return `${openTime} – ${closeTime}`;
                }).join(', ');

                googlePlacesFormat.weekdayDescriptions.push(`${day.day.toLowerCase()}: ${periodsText}`);
            } else {
                googlePlacesFormat.weekdayDescriptions.push(`${day.day.toLowerCase()}: ${t('Fermé')}`);
            }
        });

        analyticsManager.logSaveHoursTag(props.storyId);    
        props.onSave(googlePlacesFormat);
        props.onClose();
    };

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            PaperProps={{
                style: {                    
                    padding: '20px',                    
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }
            }}
        >
            <DialogTitle style={{ textAlign: 'center', fontSize: 24 }}>
                {t('ChooseMyHoursDialog')}
            </DialogTitle>
            <DialogContent style={{ marginTop: 0, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center '}}>
                <MaterialButton id="stickers-btn" style="text" onClick={() => {props.showPremium();}} value={t('ChooseMyHoursDialogHelp')}/>
                <LineBreak />
                {hours.map((day, dayIndex) => (
                    <div key={day.day} style={{ marginBottom: '20px' }}>                                                
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <div style={{ minWidth: 120, marginRight: '10px', padding: '8px 0' }}>                                
                                {t(day.day)}
                            </div>
                            <FormControl style={{ minWidth: 120 }}>                                
                                <Select
                                    value={day.open}
                                    onChange={(e) => handleDayChange(dayIndex, 'open', e.target.value)}
                                >
                                    <MenuItem value={true}>{t('Open')}</MenuItem>
                                    <MenuItem value={false}>{t('Closed')}</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                        {day.open && day.periods.map((period, periodIndex) => (
                            <div key={periodIndex} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                <FormControl style={{ minWidth: 120, marginRight: '10px' }}>                                    
                                    <Select
                                        value={period.open.time}
                                        onChange={(e) => handlePeriodChange(dayIndex, periodIndex, 'open', e.target.value)}
                                    >
                                        {TIME_SLOTS.map((time) => (
                                            <MenuItem key={time} value={time}>{formatTime(time, browserLocales(true)[0])}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl style={{ minWidth: 120, marginRight: '10px' }}>                                    
                                    <Select
                                        value={period.close.time}
                                        onChange={(e) => handlePeriodChange(dayIndex, periodIndex, 'close', e.target.value)}
                                    >
                                        {TIME_SLOTS.map((time) => (
                                            <MenuItem key={time} value={time}>{formatTime(time, browserLocales(true)[0])}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {day.periods.length > 1 && (
                                    <Button
                                        onClick={() => {analyticsManager.logDeleteHoursPeriodTag(props.storyId);removePeriod(dayIndex, periodIndex);}}
                                        color="error"
                                    >
                                        {t('Delete')}
                                    </Button>
                                )}
                            </div>
                        ))}
                        {day.open && (
                            <Button
                                onClick={() => addPeriod(dayIndex)}
                                color="primary"
                                style={{ marginTop: '10px' }}
                            >
                                {t('AddPeriod')}
                            </Button>
                        )}
                    </div>
                ))}
            </DialogContent>
            <DialogActions style={{ justifyContent: 'center' }}>
                <MaterialButton
                    style="filled"
                    onClick={handleSave}
                    value={t('SaveHours')}
                />
            </DialogActions>
        </Dialog>
    );
}

export default RestaurantHoursDialog; 